import { useEffect, useRef, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CreditCard, DollarSign, ChevronRight, CheckCircle2, ExternalLink } from "lucide-react";

export default function Financials() {
  const cherryRef = useRef<HTMLDivElement>(null);
  const [cherryLoaded, setCherryLoaded] = useState(false);

  // Inject the Cherry widget script once on mount
  useEffect(() => {
    // Cherry widget loader
    const w = window as any;
    w._hw = w._hw || function () {
      (w._hw.q = w._hw.q || []).push(arguments);
    };
    const js = document.createElement("script");
    js.id = "_hw";
    js.src = "https://files.withcherry.com/widgets/widget.js";
    js.async = true;
    document.head.appendChild(js);

    // Initialize Cherry with the clinic's configuration
    w._hw("init", {
      debug: false,
      variables: {
        slug: 'woods-chiropractic-center',
        name: "Woods Spine & Wellness",
        images: [37],
        customLogo: '',
        defaultPurchaseAmount: 1000,
        customImage: '',
        imageCategory: 'other',
        language: 'en',
      },
      styles: {
        primaryColor: '#0E202F',
        secondaryColor: '#0E202F10',
        fontFamily: 'Open Sans',
        headerFontFamily: 'Open Sans',
      }
    }, ['hero', 'calculator', 'howitworks', 'faq']);

    // Mark loaded after a short delay to allow widget to render into divs
    const t = setTimeout(() => setCherryLoaded(true), 800);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead
        title="Financing Options | Woods Spine & Wellness | Longview, TX"
        description="Explore flexible financing options at Woods Spine & Wellness in Longview, TX. We offer CareCredit and Cherry payment plans to make your care affordable."
        canonical="https://woodsspine.com/financials"
        ogImage="https://vibe.filesafe.space/1774569058151009111/assets/58600117-1663-43e4-98fd-7b421e93ecd3.png"
        schemaJson={JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          "name": "Woods Spine & Wellness",
          "description": "Financing options for chiropractic and wellness services including CareCredit and Cherry payment plans.",
          "url": "https://woodsspine.com/financials",
          "telephone": "+1-903-758-0000",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Longview",
            "addressRegion": "TX",
            "addressCountry": "US"
          },
          "paymentAccepted": "Credit Card, CareCredit, Cherry Financing"
        })}
      />
      <Navbar />
      <main id="main-content" className="flex-grow">
        {/* Hero */}
        <section className="bg-primary text-primary-foreground py-16">
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/10 mb-6">
              <DollarSign className="h-7 w-7 text-accent" />
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Financing Options
            </h1>
            <p className="text-lg text-primary-foreground/80 mb-2">
              At Woods Spine & Wellness, we believe cost should never stand between you and the care you need.
            </p>
            <p className="text-primary-foreground/70">
              That's why we offer flexible financing through CareCredit and Cherry — two trusted payment plan providers that let you break your treatment into manageable monthly payments.
            </p>
          </div>
        </section>

        {/* Financing Options Overview */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Cherry Card */}
              <div className="bg-card border border-border rounded-2xl p-8 shadow-lg flex flex-col">
                <div className="flex items-center gap-4 mb-5">
                  <img 
                    src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/attachments/60dbd88d-4851-47da-a224-7de9302f14f1.png&w=200&output=webp&q=80" 
                    alt="Cherry Logo" 
                    width="160"
                    height="50"
                    className="h-12 w-auto object-contain"
                  />
                </div>
                <p className="text-muted-foreground mb-5">
                  Cherry offers simple, transparent payment plans for healthcare services. With instant approval decisions and no hard credit check, Cherry makes it easy to get the care you need today and pay over time.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start text-sm"><CheckCircle2 className="h-5 w-5 text-accent mr-2 mt-0.5 shrink-0" /> Instant approval decisions</li>
                  <li className="flex items-start text-sm"><CheckCircle2 className="h-5 w-5 text-accent mr-2 mt-0.5 shrink-0" /> Flexible payment plan options</li>
                  <li className="flex items-start text-sm"><CheckCircle2 className="h-5 w-5 text-accent mr-2 mt-0.5 shrink-0" /> No hard credit check required</li>
                  <li className="flex items-start text-sm"><CheckCircle2 className="h-5 w-5 text-accent mr-2 mt-0.5 shrink-0" /> Manage your account entirely online</li>
                </ul>
                <p className="text-sm text-muted-foreground mb-4">
                  Use the Cherry calculator and application tool below to see your payment options instantly.
                </p>
                <Button
                  className="bg-accent text-white hover:bg-accent/90 mt-auto"
                  onClick={() => document.getElementById('cherry-widget')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                  Apply with Cherry <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>

              {/* CareCredit Card */}
              <div className="bg-card border border-border rounded-2xl p-8 shadow-lg flex flex-col">
                <div className="flex items-center gap-4 mb-5">
                  <img 
                    src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/attachments/fd76daf6-e892-4b02-9f80-b1a8e522c357.png&w=200&output=webp&q=80" 
                    alt="CareCredit Logo" 
                    width="160"
                    height="50"
                    className="h-12 w-auto object-contain"
                  />
                </div>
                <p className="text-muted-foreground mb-5">
                  CareCredit is a healthcare credit card designed specifically for health and wellness expenses. It offers special financing options so you can pay for treatments over time with convenient monthly payments.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start text-sm"><CheckCircle2 className="h-5 w-5 text-accent mr-2 mt-0.5 shrink-0" /> Special financing options available</li>
                  <li className="flex items-start text-sm"><CheckCircle2 className="h-5 w-5 text-accent mr-2 mt-0.5 shrink-0" /> Use for deductibles, co-pays, and treatments not covered by insurance</li>
                  <li className="flex items-start text-sm"><CheckCircle2 className="h-5 w-5 text-accent mr-2 mt-0.5 shrink-0" /> Quick and easy online application</li>
                  <li className="flex items-start text-sm"><CheckCircle2 className="h-5 w-5 text-accent mr-2 mt-0.5 shrink-0" /> Accepted at thousands of healthcare providers nationwide</li>
                </ul>
                <Button asChild className="bg-accent text-white hover:bg-accent/90 mt-auto">
                  <a href="https://www.carecredit.com/go/645FGN/?dtc=DS1X&sitecode=h2mdo9bg10" target="_blank" rel="noopener noreferrer">
                    Apply with CareCredit <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Cherry Widget Section */}
        <section id="cherry-widget" className="py-16 bg-secondary/30 scroll-mt-20">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-primary mb-3">Apply with Cherry</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                See your payment options in seconds. Use the calculator below to estimate your monthly payments, then complete the quick application — no hard credit check required.
              </p>
            </div>

            {/* Cherry Widget Embed */}
            <div className="bg-card rounded-2xl shadow-lg border border-border overflow-visible">
              {!cherryLoaded && (
                <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                  <div className="w-10 h-10 border-4 border-secondary border-t-primary rounded-full animate-spin mb-4"></div>
                  <p>Loading Cherry payment options...</p>
                </div>
              )}
              {/* Cherry font preconnect */}
              <link href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap" rel="stylesheet" />
              <div ref={cherryRef} className={cherryLoaded ? "cherry-widget-container w-full" : "hidden"}>
                <div id="all" className="w-full"></div>
                <div id="hero" className="w-full"></div>
                <div id="calculator" className="w-full"></div>
                <div id="howitworks" className="w-full"></div>
                <div id="testimony" className="w-full"></div>
                <div id="faq" className="w-full"></div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Have Questions About Financing?</h2>
            <p className="text-primary-foreground/80 mb-8">
              Our friendly front desk team is happy to help you understand your options and choose the plan that works best for your budget. Give us a call or book an appointment today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-accent text-white hover:bg-accent/90 border-0" asChild>
                <Link to="/appointment-scheduling">
                  Book an Appointment <ChevronRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white" asChild>
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
